<template>
  <v-dialog v-model="localModelValue" max-width="580px" scrollable>
    <v-card class="poster-modal-card">
      <div class="poster-modal-header">
        <v-btn
          icon
          class="close-button"
          aria-label="Close"
          role="button"
          @click="close"
          variant="plain"
        >
          <v-icon size="24" aria-hidden="true">mdi-close</v-icon>
        </v-btn>
      </div>

      <div class="poster-modal-content">
        <div class="poster-image-container" ref="imageContainer">
          <div class="poster-image-wrapper" :style="imageWrapperStyle">
            <img
              :src="currentImage"
              :alt="`Poster ${currentIndex + 1}`"
              class="poster-image"
              ref="posterImage"
              @load="onImageLoad"
              @mousedown="startDrag"
              @touchstart="startDragTouch"
              @wheel="onWheel"
              draggable="false"
            />
          </div>
        </div>

        <div class="poster-footer">
          <button
            type="button"
            role="button"
            class="zoom-button-footer"
            @click="zoomOut"
            :disabled="scale <= minScale"
            :class="{ disabled: scale <= minScale }"
            aria-label="Zoom out"
          >
            <SearchMinusIcon
              width="20"
              height="20"
              color="currentColor"
              aria-hidden="true"
            />
          </button>
          <button
            type="button"
            role="button"
            class="zoom-button-footer"
            @click="zoomIn"
            :disabled="scale >= maxScale"
            :class="{ disabled: scale >= maxScale }"
            aria-label="Zoom in"
          >
            <SearchPlusIcon
              width="24"
              height="24"
              color="currentColor"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
      <div class="navigation-arrows">
        <button
          type="button"
          role="button"
          class="nav-arrow-button nav-arrow-left"
          @click="previousImage"
          :disabled="currentIndex === 0"
          :class="{ disabled: currentIndex === 0 }"
          aria-label="Previous image"
        >
          <ChevronLeftIcon
            width="24"
            height="24"
            color="currentColor"
            aria-hidden="true"
          />
        </button>
        <button
          type="button"
          role="button"
          class="nav-arrow-button nav-arrow-right"
          @click="nextImage"
          :disabled="currentIndex === images.length - 1"
          :class="{ disabled: currentIndex === images.length - 1 }"
          aria-label="Next image"
        >
          <ChevronRightIcon
            width="24"
            height="24"
            color="currentColor"
            aria-hidden="true"
          />
        </button>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import {
  computed,
  defineProps,
  defineEmits,
  ref,
  watch,
  onUnmounted,
} from "vue";
import SearchMinusIcon from "../icons/SearchMinusIcon.vue";
import SearchPlusIcon from "../icons/SearchPlusIcon.vue";
import { ChevronLeftIcon } from "../icons";
import ChevronRightIcon from "../icons/ChevronRightIcon.vue";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  images: {
    type: Array,
    default: () => [],
  },
  initialIndex: {
    type: Number,
    default: 0,
  },
  title: {
    type: String,
    default: "Activity Poster",
  },
});

const emit = defineEmits(["update:modelValue", "close"]);

const localModelValue = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const currentIndex = ref(props.initialIndex);
const scale = ref(1);
const translateX = ref(0);
const translateY = ref(0);
const isDragging = ref(false);
const dragStart = ref({ x: 0, y: 0 });
const imageContainer = ref(null);
const posterImage = ref(null);

const minScale = 0.5;
const maxScale = 3;

const currentImage = computed(() => {
  return props.images[currentIndex.value] || "";
});

const imageWrapperStyle = computed(() => {
  return {
    transform: `translate(${translateX.value}px, ${translateY.value}px) scale(${scale.value})`,
    transition: isDragging.value ? "none" : "transform 0.2s ease-out",
  };
});

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal) {
      currentIndex.value = Math.max(
        0,
        Math.min(props.initialIndex, props.images.length - 1)
      );
      resetZoom();
    }
  }
);

watch(
  () => props.initialIndex,
  (newIndex) => {
    if (props.modelValue) {
      currentIndex.value = Math.max(
        0,
        Math.min(newIndex, props.images.length - 1)
      );
      resetZoom();
    }
  }
);

watch(currentIndex, () => {
  resetZoom();
});

const onImageLoad = () => {
  resetZoom();
};

const zoomIn = () => {
  if (scale.value < maxScale) {
    scale.value = Math.min(scale.value + 0.25, maxScale);
  }
};

const zoomOut = () => {
  if (scale.value > minScale) {
    scale.value = Math.max(scale.value - 0.25, minScale);
    if (scale.value === 1) {
      resetPosition();
    }
  }
};

const resetZoom = () => {
  scale.value = 1;
  resetPosition();
};

const resetPosition = () => {
  translateX.value = 0;
  translateY.value = 0;
};

const startDrag = (e) => {
  if (scale.value > 1) {
    e.preventDefault();
    isDragging.value = true;
    dragStart.value = {
      x: e.clientX - translateX.value,
      y: e.clientY - translateY.value,
    };
    document.addEventListener("mousemove", onDrag);
    document.addEventListener("mouseup", endDrag);
  }
};

const startDragTouch = (e) => {
  if (scale.value > 1 && e.touches.length === 1) {
    e.preventDefault();
    isDragging.value = true;
    dragStart.value = {
      x: e.touches[0].clientX - translateX.value,
      y: e.touches[0].clientY - translateY.value,
    };
    document.addEventListener("touchmove", onDragTouch);
    document.addEventListener("touchend", endDrag);
  }
};

const onDrag = (e) => {
  if (isDragging.value && scale.value > 1) {
    e.preventDefault();
    translateX.value = e.clientX - dragStart.value.x;
    translateY.value = e.clientY - dragStart.value.y;

    const container = imageContainer.value;
    if (container) {
      const containerRect = container.getBoundingClientRect();
      const imageRect = posterImage.value?.getBoundingClientRect();
      if (imageRect) {
        const scaledWidth = imageRect.width;
        const scaledHeight = imageRect.height;
        const maxX = (scaledWidth - containerRect.width) / 2;
        const maxY = (scaledHeight - containerRect.height) / 2;

        translateX.value = Math.max(-maxX, Math.min(maxX, translateX.value));
        translateY.value = Math.max(-maxY, Math.min(maxY, translateY.value));
      }
    }
  }
};

const onDragTouch = (e) => {
  if (isDragging.value && scale.value > 1 && e.touches.length === 1) {
    e.preventDefault();
    translateX.value = e.touches[0].clientX - dragStart.value.x;
    translateY.value = e.touches[0].clientY - dragStart.value.y;

    const container = imageContainer.value;
    if (container) {
      const containerRect = container.getBoundingClientRect();
      const imageRect = posterImage.value?.getBoundingClientRect();
      if (imageRect) {
        const scaledWidth = imageRect.width;
        const scaledHeight = imageRect.height;
        const maxX = (scaledWidth - containerRect.width) / 2;
        const maxY = (scaledHeight - containerRect.height) / 2;

        translateX.value = Math.max(-maxX, Math.min(maxX, translateX.value));
        translateY.value = Math.max(-maxY, Math.min(maxY, translateY.value));
      }
    }
  }
};

const endDrag = () => {
  isDragging.value = false;
  document.removeEventListener("mousemove", onDrag);
  document.removeEventListener("mouseup", endDrag);
  document.removeEventListener("touchmove", onDragTouch);
  document.removeEventListener("touchend", endDrag);
};

const onWheel = (e) => {
  if (scale.value <= 1 && e.deltaY < 0) return;

  e.preventDefault();
  const delta = e.deltaY > 0 ? -0.1 : 0.1;
  const newScale = Math.max(minScale, Math.min(maxScale, scale.value + delta));

  if (newScale !== scale.value) {
    const container = imageContainer.value;
    if (container) {
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;

      const scaleChange = newScale / scale.value;
      translateX.value = translateX.value * scaleChange + x * (1 - scaleChange);
      translateY.value = translateY.value * scaleChange + y * (1 - scaleChange);
    }

    scale.value = newScale;

    if (scale.value === 1) {
      resetPosition();
    }
  }
};

const previousImage = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--;
  }
};

const nextImage = () => {
  if (currentIndex.value < props.images.length - 1) {
    currentIndex.value++;
  }
};

const close = () => {
  localModelValue.value = false;
  emit("close");
  resetZoom();
};

onUnmounted(() => {
  endDrag();
});
</script>

<style scoped>
.poster-modal-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  background-color: #dfe2e7;
  position: relative;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  padding: 24px;
}

.poster-modal-header {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;
}

.close-button {
  min-width: 40px;
  width: 40px;
  height: 40px;
  padding: 0;
  color: #3b4648;
}

.close-button:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.poster-modal-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  gap: 24px;
  align-items: center;
}

.poster-image-container {
  flex: 1;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  border-radius: 0;
  position: relative;
  width: 100%;
  max-width: 410px;
  cursor: grab;
  border-radius: 12px;
}

.poster-image-container:active {
  cursor: grabbing;
}

.poster-image-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  transform-origin: center center;
  width: 100%;
  height: 100%;
}

.poster-image {
  max-width: 100%;
  max-height: 100%;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  user-select: none;
  pointer-events: auto;
}

.navigation-arrows {
  position: absolute;
  padding: 0 14px;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
  z-index: 10;
}

.nav-arrow-button {
  width: 56px;
  height: 56px;
  background-color: #dfe2e7;
  color: var(--primary-blue);
  border-radius: 12px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  pointer-events: auto;
  padding: 0;
}

.nav-arrow-button:hover:not(.disabled) {
  background-color: var(--primary-blue);
  color: white;
  transform: scale(1.05);
}

.nav-arrow-button.disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.poster-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-shrink: 0;
}

.zoom-button-footer {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  border: 1px solid var(--primary-blue);
  background-color: transparent;
  color: var(--primary-blue);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
}

.zoom-button-footer:hover:not(.disabled) {
  background-color: var(--primary-blue);
  color: white;
  transform: scale(1.05);
}

.zoom-button-footer.disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.close-button:focus-visible,
.zoom-button-footer:focus-visible,
.nav-arrow-button:focus-visible {
  outline: 2px solid var(--primary-blue);
  outline-offset: 2px;
}

@media (max-width: 768px) {
  .poster-modal-card {
    padding: 16px;
    gap: 16px;
  }

  .poster-image-container {
    max-width: 100%;
    height: 600px;
  }

  .navigation-arrows {
    top: auto;
    bottom: 16px;
    left: 16px;
    right: 16px;
    padding: 0;
    width: auto;
    max-width: none;
    transform: none;
  }

  .nav-arrow-button {
    width: 48px;
    height: 48px;
    background-color: transparent;
    border: 1px solid var(--primary-blue);
    border-radius: 8px;
  }

  .zoom-button-footer {
    width: 48px;
    height: 48px;
    border-radius: 8px;
  }
}

@media (max-width: 480px) {
  .poster-image-container {
    height: 500px;
  }

  .nav-arrow-button {
    width: 44px;
    height: 44px;
    border-radius: 8px;
  }

  .zoom-button-footer {
    width: 44px;
    height: 44px;
    border-radius: 8px;
  }
}
</style>
